/** 认证状态：session 变化时触发重渲染；API 层通过 getAccessToken() 拿 token。 */
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react'
import type { Session, User } from '@supabase/auth-js'
import { SUPABASE_CONFIGURED, authHeader, getSupabase } from './supabase'
import { consumePostLoginRedirect } from './postLoginRedirect'

const UNAUTHORIZED_FLAG = 'unauthorized_user'

export function consumeUnauthorizedFlag(): boolean {
  try {
    if (sessionStorage.getItem(UNAUTHORIZED_FLAG) === '1') {
      sessionStorage.removeItem(UNAUTHORIZED_FLAG)
      return true
    }
  } catch {
    /* sessionStorage 不可用时忽略 */
  }
  return false
}

interface AuthState {
  configured: boolean
  loading: boolean
  session: Session | null
  user: User | null
  /** null=未检查（无 session 或检查中），true=白名单用户，false=非白名单 */
  authorized: boolean | null
  signInWithGoogle: () => Promise<void>
  signInWithEmail: (email: string, password: string) => Promise<string | null>
  signUpWithEmail: (email: string, password: string) => Promise<string | null>
  signOut: () => Promise<void>
}

const AuthContext = createContext<AuthState | null>(null)

export function AuthProvider({ children }: { children: ReactNode }) {
  const [loading, setLoading] = useState(true)
  const [session, setSession] = useState<Session | null>(null)
  const [authorized, setAuthorized] = useState<boolean | null>(null)

  // 白名单自检：session 就绪后调 /api/auth/me，非白名单 403 → 登出并标记
  const checkAuthorized = useCallback(async () => {
    try {
      const { API_BASE } = await import('../lib/http')
      const resp = await fetch(`${API_BASE}/api/auth/me`, { headers: authHeader() })
      if (resp.status === 403) {
        try {
          sessionStorage.setItem(UNAUTHORIZED_FLAG, '1')
        } catch {
          /* 忽略 */
        }
        const client = getSupabase()
        if (client) await client.signOut()
        setSession(null)
        setAuthorized(false)
        return
      }
      setAuthorized(resp.ok)
    } catch {
      // 网络异常不断言未授权，保持 null 让页面按原逻辑展示
      setAuthorized(null)
    }
  }, [])

  useEffect(() => {
    const client = getSupabase()
    if (!client) {
      setLoading(false)
      return
    }
    client.getSession().then(({ data }) => {
      setSession(data.session)
      // session 恢复完成（含 Google OAuth 整页重定向回来）后回跳原路由
      if (data.session) {
        consumePostLoginRedirect()
        void checkAuthorized()
      }
      setLoading(false)
    }).catch(() => {
      // getSession 失败（如 storage 被破坏）也不能卡死在 loading
      setSession(null)
      setLoading(false)
    })
    const { data: sub } = client.onAuthStateChange((_event, s) => {
      setSession(s)
      if (s) void checkAuthorized()
      else setAuthorized(null)
    })
    return () => sub.subscription.unsubscribe()
  }, [checkAuthorized])

  const signInWithGoogle = useCallback(async () => {
    const client = getSupabase()
    if (!client) return
    await client.signInWithOAuth({
      provider: 'google',
      options: { redirectTo: window.location.origin + window.location.pathname },
    })
  }, [])

  const signInWithEmail = useCallback(async (email: string, password: string) => {
    const client = getSupabase()
    if (!client) return '认证未配置'
    const { error } = await client.signInWithPassword({ email, password })
    return error ? error.message : null
  }, [])

  const signUpWithEmail = useCallback(async (email: string, password: string) => {
    const client = getSupabase()
    if (!client) return '认证未配置'
    const { error } = await client.signUp({ email, password })
    return error ? error.message : null
  }, [])

  const signOut = useCallback(async () => {
    const client = getSupabase()
    if (client) await client.signOut()
    setSession(null)
  }, [])

  const value = useMemo<AuthState>(
    () => ({
      configured: SUPABASE_CONFIGURED,
      loading,
      session,
      user: session?.user ?? null,
      authorized,
      signInWithGoogle,
      signInWithEmail,
      signUpWithEmail,
      signOut,
    }),
    [loading, session, authorized, signInWithGoogle, signInWithEmail, signUpWithEmail, signOut],
  )
  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}

export function useAuth(): AuthState {
  const ctx = useContext(AuthContext)
  if (!ctx) throw new Error('useAuth 必须在 <AuthProvider> 内使用')
  return ctx
}
